import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const GOAL = 2;

/** Returns days (0=Mon..6=Sun) the user has studied this week. */
export function useWeeklyStreak() {
  const { user } = useAuth();
  const [studiedDays, setStudiedDays] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);

    // Compute Monday 00:00 local
    const now = new Date();
    const day = now.getDay(); // 0=Sun..6=Sat
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    const weekEnd = new Date(monday);
    weekEnd.setDate(monday.getDate() + 7);

    const dayIndex = (d: Date) => {
      const js = d.getDay();
      return js === 0 ? 6 : js - 1;
    };

    (async () => {
      const days = new Set<number>();
      const [sessions, quizzes, exams] = await Promise.all([
        supabase
          .from("flashcard_study_sessions")
          .select("created_at")
          .eq("user_id", user.id)
          .gte("created_at", monday.toISOString())
          .lt("created_at", weekEnd.toISOString()),
        supabase
          .from("quiz_attempts")
          .select("completed_at")
          .eq("user_id", user.id)
          .gte("completed_at", monday.toISOString())
          .lt("completed_at", weekEnd.toISOString()),
        supabase
          .from("exam_attempts")
          .select("submitted_at")
          .eq("user_id", user.id)
          .gte("submitted_at", monday.toISOString())
          .lt("submitted_at", weekEnd.toISOString()),
      ]);

      const push = (ts?: string | null) => {
        if (!ts) return;
        days.add(dayIndex(new Date(ts)));
      };
      sessions.data?.forEach((r: any) => push(r.created_at));
      quizzes.data?.forEach((r: any) => push(r.completed_at));
      exams.data?.forEach((r: any) => push(r.submitted_at));

      if (!cancelled) {
        setStudiedDays(days);
        setLoading(false);
      }
    })().catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [user]);

  return { studiedDays, count: studiedDays.size, goal: GOAL, loading };
}
